import React, { useState } from 'react';
import { useAIModel } from '../contexts/AIModelContext';
import ModelSelector from './ModelSelector';
import axios from 'axios';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Progress } from '@/components/ui/progress';
import { ScrollArea } from '@/components/ui/scroll-area';
import { 
  Search, TrendingUp, TrendingDown, Activity, Target, 
  Brain, Zap, AlertTriangle, CheckCircle2, XCircle,
  BarChart3, LineChart, ArrowUp, ArrowDown, Minus,
  DollarSign, Newspaper, RefreshCw, Shield, Percent,
  Eye, ExternalLink
} from 'lucide-react';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const MovementAnalyzer = () => {
  const [symbol, setSymbol] = useState('');
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);
  const { model: aiModel, setModel: setAiModel } = useAIModel();

  const analyzeStock = async () => {
    if (!symbol.trim()) {
      toast.error('Please enter a stock symbol');
      return;
    }

    setLoading(true);
    toast.info(`Analyzing ${symbol.toUpperCase()}... This may take 15-30 seconds.`);

    try {
      const response = await axios.post(`${API}/movement-analyzer/analyze`, {
        symbol: symbol.toUpperCase(),
        ai_model: aiModel
      }, { timeout: 60000 });

      setResult(response.data);
      toast.success(`Analysis complete for ${symbol.toUpperCase()}`);
    } catch (error) {
      console.error('Error analyzing:', error);
      toast.error(error.response?.data?.detail || 'Failed to analyze stock');
    } finally {
      setLoading(false);
    }
  };

  const getOutlookColor = (outlook) => {
    switch (outlook?.toLowerCase()) {
      case 'strongly bullish': return 'bg-green-500 text-white';
      case 'bullish': return 'bg-green-400 text-white';
      case 'neutral': return 'bg-gray-400 text-white';
      case 'bearish': return 'bg-red-400 text-white';
      case 'strongly bearish': return 'bg-red-500 text-white';
      default: return 'bg-gray-400 text-white';
    }
  };

  const getOutlookIcon = (outlook) => {
    switch (outlook?.toLowerCase()) {
      case 'strongly bullish':
      case 'bullish':
        return <TrendingUp className="h-5 w-5" />;
      case 'bearish':
      case 'strongly bearish':
        return <TrendingDown className="h-5 w-5" />;
      default:
        return <Minus className="h-5 w-5" />;
    }
  };

  const getConfidenceBadge = (confidence) => {
    switch (confidence?.toLowerCase()) {
      case 'high': return <Badge className="bg-green-100 text-green-800">High Confidence</Badge>;
      case 'medium': return <Badge className="bg-yellow-100 text-yellow-800">Medium Confidence</Badge>;
      case 'low': return <Badge className="bg-red-100 text-red-800">Low Confidence</Badge>;
      default: return <Badge variant="outline">{confidence}</Badge>;
    }
  };

  const getRSIColor = (rsi) => {
    if (rsi > 70) return 'text-red-500';
    if (rsi < 30) return 'text-green-500';
    return 'text-gray-700';
  };

  const getTrendBadge = (trend) => {
    switch (trend?.toLowerCase()) {
      case 'strong bullish': return <Badge className="bg-green-500 text-white">Strong Bullish</Badge>;
      case 'bullish': return <Badge className="bg-green-400 text-white">Bullish</Badge>;
      case 'strong bearish': return <Badge className="bg-red-500 text-white">Strong Bearish</Badge>;
      case 'bearish': return <Badge className="bg-red-400 text-white">Bearish</Badge>;
      default: return <Badge variant="outline">Neutral</Badge>;
    }
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <Card className="glass-effect border-0 shadow-lg bg-gradient-to-r from-amber-50 to-orange-50">
        <CardHeader>
          <div className="flex items-center space-x-3">
            <div className="p-3 bg-gradient-to-br from-amber-500 to-orange-600 rounded-xl">
              <Target className="h-8 w-8 text-white" />
            </div>
            <div>
              <CardTitle className="text-2xl">Stock Movement Analyzer</CardTitle>
              <CardDescription className="text-base">
                AI-powered analysis combining technicals, options flow, and news to predict directional movement
              </CardDescription>
            </div>
          </div>
        </CardHeader>
      </Card>

      {/* Search Input */}
      <Card className="glass-effect border-0 shadow-lg">
        <CardContent className="pt-6">
          <div className="flex flex-col sm:flex-row gap-4">
            <div className="flex-1">
              <Label className="sr-only">Stock Symbol</Label>
              <Input
                placeholder="Enter stock symbol (e.g., AAPL, TSLA, NVDA)"
                value={symbol}
                onChange={(e) => setSymbol(e.target.value.toUpperCase())}
                onKeyPress={(e) => e.key === 'Enter' && analyzeStock()}
                className="text-lg h-12"
              />
            </div>
            <div className="flex items-center space-x-3">
              <Brain className="h-5 w-5 text-amber-600" />
              <ModelSelector />
            </div>
            <Button
              onClick={analyzeStock}
              disabled={loading}
              className="bg-gradient-to-r from-amber-500 to-orange-600 hover:from-amber-600 hover:to-orange-700 h-12 px-8 text-lg"
            >
              {loading ? (
                <>
                  <RefreshCw className="h-5 w-5 mr-2 animate-spin" />
                  Analyzing...
                </>
              ) : (
                <>
                  <Zap className="h-5 w-5 mr-2" />
                  Analyze
                </>
              )}
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Results */}
      {result && (
        <div className="space-y-6 fade-in">
          {/* AI Analysis Summary */}
          <Card className="glass-effect border-0 shadow-xl overflow-hidden">
            <div className={`h-2 ${getOutlookColor(result.ai_analysis?.outlook)}`} />
            <CardHeader>
              <div className="flex items-center justify-between">
                <div className="flex items-center space-x-4">
                  <div className={`p-3 rounded-xl ${getOutlookColor(result.ai_analysis?.outlook)}`}>
                    {getOutlookIcon(result.ai_analysis?.outlook)}
                  </div>
                  <div>
                    <CardTitle className="text-2xl flex items-center space-x-3">
                      <span>{result.symbol}</span>
                      <span className="text-gray-400">|</span>
                      <span className="text-amber-600">${result.current_price}</span>
                    </CardTitle>
                    <CardDescription className="text-base">{result.name}</CardDescription>
                  </div>
                </div>
                <div className="text-right space-y-2">
                  <Badge className={`text-lg px-4 py-1 ${getOutlookColor(result.ai_analysis?.outlook)}`}>
                    {result.ai_analysis?.outlook || 'Analyzing...'}
                  </Badge>
                  <div>{getConfidenceBadge(result.ai_analysis?.confidence)}</div>
                </div>
              </div>
            </CardHeader>
            <CardContent>
              {/* Price Target */}
              {result.ai_analysis?.price_target_low && result.ai_analysis?.price_target_high && (
                <div className="mb-6 bg-gradient-to-r from-slate-50 to-amber-50 rounded-lg p-4">
                  <Label className="text-sm text-gray-600 mb-2 block">7-Day Price Target Range</Label>
                  <div className="flex items-center justify-between">
                    <span className="text-2xl font-bold text-red-500">${result.ai_analysis.price_target_low}</span>
                    <div className="flex-1 mx-4">
                      <div className="relative h-3 bg-gradient-to-r from-red-200 via-yellow-200 to-green-200 rounded-full">
                        <div 
                          className="absolute top-1/2 transform -translate-y-1/2 w-4 h-4 bg-amber-500 rounded-full border-2 border-white shadow-lg"
                          style={{
                            left: `${((result.current_price - result.ai_analysis.price_target_low) / 
                                    (result.ai_analysis.price_target_high - result.ai_analysis.price_target_low)) * 100}%`
                          }}
                        />
                      </div>
                    </div>
                    <span className="text-2xl font-bold text-green-500">${result.ai_analysis.price_target_high}</span>
                  </div>
                  <p className="text-center text-sm text-gray-500 mt-2">Current: ${result.current_price}</p>
                </div>
              )}

              {/* Summary */}
              {result.ai_analysis?.summary && (
                <div className="bg-blue-50 rounded-lg p-4 mb-4 border border-blue-200">
                  <p className="text-blue-800">{result.ai_analysis.summary}</p>
                </div>
              )}

              {/* Recommended Action */}
              {result.ai_analysis?.recommended_action && (
                <div className="bg-amber-50 rounded-lg p-4 border border-amber-200">
                  <p className="text-sm font-semibold text-amber-800 mb-1">💡 Recommended Action for Options Traders</p>
                  <p className="text-amber-700">{result.ai_analysis.recommended_action}</p>
                </div>
              )}
            </CardContent>
          </Card>

          {/* Main Analysis Grid */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* Technical Indicators */}
            <Card className="glass-effect border-0 shadow-lg">
              <CardHeader>
                <CardTitle className="flex items-center text-lg">
                  <LineChart className="h-5 w-5 mr-2 text-blue-600" />
                  Technical Indicators
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                {/* Trend */}
                <div className="flex items-center justify-between">
                  <span className="text-gray-600">Overall Trend</span>
                  {getTrendBadge(result.technical_indicators?.trend)}
                </div>

                {/* RSI */}
                <div className="space-y-1">
                  <div className="flex items-center justify-between">
                    <span className="text-gray-600">RSI (14)</span>
                    <span className={`font-bold ${getRSIColor(result.technical_indicators?.rsi)}`}>
                      {result.technical_indicators?.rsi} ({result.technical_indicators?.rsi_signal})
                    </span>
                  </div>
                  <Progress value={result.technical_indicators?.rsi} className="h-2" />
                  <div className="flex justify-between text-xs text-gray-400">
                    <span>Oversold (30)</span>
                    <span>Overbought (70)</span>
                  </div>
                </div>

                {/* MACD */}
                <div className="flex items-center justify-between">
                  <span className="text-gray-600">MACD Signal</span>
                  <Badge className={result.technical_indicators?.macd_crossover === 'Bullish' ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'}>
                    {result.technical_indicators?.macd_crossover}
                  </Badge>
                </div>

                {/* Moving Averages */}
                <div className="grid grid-cols-2 gap-4 pt-2">
                  <div className="bg-slate-50 rounded-lg p-3">
                    <p className="text-xs text-gray-500">SMA 20</p>
                    <p className="font-bold">${result.technical_indicators?.sma_20}</p>
                    <Badge variant="outline" className="mt-1 text-xs">
                      {result.technical_indicators?.price_vs_sma20}
                    </Badge>
                  </div>
                  <div className="bg-slate-50 rounded-lg p-3">
                    <p className="text-xs text-gray-500">SMA 50</p>
                    <p className="font-bold">${result.technical_indicators?.sma_50}</p>
                    <Badge variant="outline" className="mt-1 text-xs">
                      {result.technical_indicators?.price_vs_sma50}
                    </Badge>
                  </div>
                </div>

                {/* Volume & Bollinger */}
                <div className="grid grid-cols-2 gap-4">
                  <div className="bg-slate-50 rounded-lg p-3">
                    <p className="text-xs text-gray-500">Volume</p>
                    <p className="font-bold">{result.technical_indicators?.volume_ratio}x avg</p>
                    <Badge variant="outline" className="mt-1 text-xs">
                      {result.technical_indicators?.volume_signal}
                    </Badge>
                  </div>
                  <div className="bg-slate-50 rounded-lg p-3">
                    <p className="text-xs text-gray-500">Bollinger Position</p>
                    <p className="font-bold text-sm">{result.technical_indicators?.bollinger_position}</p>
                  </div>
                </div>

                {/* Momentum */}
                <div className="pt-2">
                  <p className="text-sm text-gray-600 mb-2">Price Momentum</p>
                  <div className="grid grid-cols-3 gap-2">
                    <div className="text-center bg-slate-50 rounded p-2">
                      <p className="text-xs text-gray-500">1 Day</p>
                      <p className={`font-bold ${result.technical_indicators?.momentum?.['1_day'] >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                        {result.technical_indicators?.momentum?.['1_day'] >= 0 ? '+' : ''}{result.technical_indicators?.momentum?.['1_day']}%
                      </p>
                    </div>
                    <div className="text-center bg-slate-50 rounded p-2">
                      <p className="text-xs text-gray-500">5 Days</p>
                      <p className={`font-bold ${result.technical_indicators?.momentum?.['5_day'] >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                        {result.technical_indicators?.momentum?.['5_day'] >= 0 ? '+' : ''}{result.technical_indicators?.momentum?.['5_day']}%
                      </p>
                    </div>
                    <div className="text-center bg-slate-50 rounded p-2">
                      <p className="text-xs text-gray-500">20 Days</p>
                      <p className={`font-bold ${result.technical_indicators?.momentum?.['20_day'] >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                        {result.technical_indicators?.momentum?.['20_day'] >= 0 ? '+' : ''}{result.technical_indicators?.momentum?.['20_day']}%
                      </p>
                    </div>
                  </div>
                </div>

                {/* Support/Resistance */}
                <div className="grid grid-cols-2 gap-4 pt-2">
                  <div className="bg-green-50 rounded-lg p-3 border border-green-200">
                    <p className="text-xs text-green-600">Support</p>
                    <p className="font-bold text-green-700">${result.technical_indicators?.support}</p>
                  </div>
                  <div className="bg-red-50 rounded-lg p-3 border border-red-200">
                    <p className="text-xs text-red-600">Resistance</p>
                    <p className="font-bold text-red-700">${result.technical_indicators?.resistance}</p>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Options Flow */}
            <Card className="glass-effect border-0 shadow-lg">
              <CardHeader>
                <CardTitle className="flex items-center text-lg">
                  <Activity className="h-5 w-5 mr-2 text-purple-600" />
                  Options Flow Analysis
                </CardTitle>
                <CardDescription>Expiration: {result.options_flow?.nearest_expiration}</CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                {/* Put/Call Ratio */}
                <div className="flex items-center justify-between">
                  <span className="text-gray-600">Put/Call Ratio</span>
                  <div className="flex items-center space-x-2">
                    <span className="font-bold">{result.options_flow?.put_call_ratio}</span>
                    <Badge className={
                      result.options_flow?.pc_signal === 'Bullish' ? 'bg-green-100 text-green-800' :
                      result.options_flow?.pc_signal === 'Bearish' ? 'bg-red-100 text-red-800' :
                      'bg-gray-100 text-gray-800'
                    }>
                      {result.options_flow?.pc_signal}
                    </Badge>
                  </div>
                </div>

                {/* Max Pain */}
                <div className="flex items-center justify-between">
                  <span className="text-gray-600">Max Pain</span>
                  <div className="flex items-center space-x-2">
                    <span className="font-bold">${result.options_flow?.max_pain}</span>
                    <Badge variant="outline">{result.options_flow?.max_pain_vs_price} price</Badge>
                  </div>
                </div>

                {/* Expected Move */}
                {result.options_flow?.expected_move && (
                  <div className="bg-purple-50 rounded-lg p-3 border border-purple-200">
                    <p className="text-xs text-purple-600">Expected Move (by expiration)</p>
                    <p className="font-bold text-purple-700">
                      ±${result.options_flow?.expected_move} ({result.options_flow?.expected_move_pct}%)
                    </p>
                  </div>
                )}

                {/* Volume Stats */}
                <div className="grid grid-cols-2 gap-4">
                  <div className="bg-green-50 rounded-lg p-3">
                    <p className="text-xs text-green-600">Call Volume</p>
                    <p className="font-bold text-green-700">{result.options_flow?.total_call_volume?.toLocaleString()}</p>
                    <p className="text-xs text-gray-500">OI: {result.options_flow?.total_call_oi?.toLocaleString()}</p>
                  </div>
                  <div className="bg-red-50 rounded-lg p-3">
                    <p className="text-xs text-red-600">Put Volume</p>
                    <p className="font-bold text-red-700">{result.options_flow?.total_put_volume?.toLocaleString()}</p>
                    <p className="text-xs text-gray-500">OI: {result.options_flow?.total_put_oi?.toLocaleString()}</p>
                  </div>
                </div>

                {/* Average IV */}
                {result.options_flow?.avg_iv && (
                  <div className="flex items-center justify-between">
                    <span className="text-gray-600">Average IV</span>
                    <span className="font-bold">{result.options_flow?.avg_iv}%</span>
                  </div>
                )}

                {/* Unusual Activity */}
                {result.options_flow?.unusual_activity?.length > 0 && (
                  <div className="pt-2">
                    <p className="text-sm font-semibold text-purple-700 mb-2">🔥 Unusual Options Activity</p>
                    <ScrollArea className="h-32">
                      <div className="space-y-2">
                        {result.options_flow.unusual_activity.map((activity, i) => (
                          <div key={i} className="flex items-center justify-between bg-slate-50 rounded px-3 py-2 text-sm">
                            <Badge className={activity.type === 'CALL' ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'}>
                              {activity.type}
                            </Badge>
                            <span>${activity.strike}</span>
                            <span className="text-gray-500">Vol: {activity.volume?.toLocaleString()}</span>
                            <span className="font-semibold">{activity.vol_oi_ratio}x OI</span>
                          </div>
                        ))}
                      </div>
                    </ScrollArea>
                  </div>
                )}
              </CardContent>
            </Card>
          </div>

          {/* Bullish/Bearish Factors & Key Levels */}
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            {/* Bullish Factors */}
            <Card className="glass-effect border-0 shadow-lg border-t-4 border-green-500">
              <CardHeader>
                <CardTitle className="flex items-center text-lg text-green-700">
                  <CheckCircle2 className="h-5 w-5 mr-2" />
                  Bullish Factors
                </CardTitle>
              </CardHeader>
              <CardContent>
                <ul className="space-y-2">
                  {result.ai_analysis?.bullish_factors?.map((factor, i) => (
                    <li key={i} className="flex items-start space-x-2 text-sm">
                      <ArrowUp className="h-4 w-4 text-green-500 mt-0.5 flex-shrink-0" />
                      <span>{factor}</span>
                    </li>
                  ))}
                </ul>
              </CardContent>
            </Card>

            {/* Bearish Factors */}
            <Card className="glass-effect border-0 shadow-lg border-t-4 border-red-500">
              <CardHeader>
                <CardTitle className="flex items-center text-lg text-red-700">
                  <XCircle className="h-5 w-5 mr-2" />
                  Bearish Factors
                </CardTitle>
              </CardHeader>
              <CardContent>
                <ul className="space-y-2">
                  {result.ai_analysis?.bearish_factors?.map((factor, i) => (
                    <li key={i} className="flex items-start space-x-2 text-sm">
                      <ArrowDown className="h-4 w-4 text-red-500 mt-0.5 flex-shrink-0" />
                      <span>{factor}</span>
                    </li>
                  ))}
                </ul>
              </CardContent>
            </Card>

            {/* Key Levels & Risks */}
            <Card className="glass-effect border-0 shadow-lg">
              <CardHeader>
                <CardTitle className="flex items-center text-lg">
                  <Target className="h-5 w-5 mr-2 text-amber-600" />
                  Key Levels & Risks
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                {result.ai_analysis?.key_levels && (
                  <div className="space-y-2">
                    <div className="flex justify-between text-sm">
                      <span className="text-red-600">Strong Resistance</span>
                      <span className="font-bold">${result.ai_analysis.key_levels.strong_resistance}</span>
                    </div>
                    <div className="flex justify-between text-sm">
                      <span className="text-red-400">Resistance</span>
                      <span className="font-bold">${result.ai_analysis.key_levels.resistance}</span>
                    </div>
                    <div className="flex justify-between text-sm bg-amber-50 px-2 py-1 rounded">
                      <span className="text-amber-600">Current Price</span>
                      <span className="font-bold">${result.current_price}</span>
                    </div>
                    <div className="flex justify-between text-sm">
                      <span className="text-green-400">Support</span>
                      <span className="font-bold">${result.ai_analysis.key_levels.support}</span>
                    </div>
                    <div className="flex justify-between text-sm">
                      <span className="text-green-600">Strong Support</span>
                      <span className="font-bold">${result.ai_analysis.key_levels.strong_support}</span>
                    </div>
                  </div>
                )}

                {result.ai_analysis?.risk_factors?.length > 0 && (
                  <div className="pt-2 border-t">
                    <p className="text-sm font-semibold text-amber-700 mb-2">⚠️ Risk Factors</p>
                    <ul className="space-y-1">
                      {result.ai_analysis.risk_factors.map((risk, i) => (
                        <li key={i} className="text-xs text-gray-600 flex items-start space-x-1">
                          <AlertTriangle className="h-3 w-3 text-amber-500 mt-0.5 flex-shrink-0" />
                          <span>{risk}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                )}
              </CardContent>
            </Card>
          </div>

          {/* News Section */}
          {result.news?.length > 0 && (
            <Card className="glass-effect border-0 shadow-lg">
              <CardHeader>
                <CardTitle className="flex items-center text-lg">
                  <Newspaper className="h-5 w-5 mr-2 text-indigo-600" />
                  Recent News
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                  {result.news.map((item, i) => (
                    <a 
                      key={i} 
                      href={item.link} 
                      target="_blank" 
                      rel="noopener noreferrer"
                      className="block bg-slate-50 rounded-lg p-3 hover:bg-slate-100 transition-colors"
                    >
                      <p className="text-sm font-medium line-clamp-2 mb-2">{item.title}</p>
                      <div className="flex items-center justify-between text-xs text-gray-500">
                        <span>{item.publisher}</span>
                        <span>{item.published}</span>
                      </div>
                    </a>
                  ))}
                </div>
              </CardContent>
            </Card>
          )}

          {/* Fundamentals */}
          <Card className="glass-effect border-0 shadow-lg">
            <CardHeader>
              <CardTitle className="flex items-center text-lg">
                <BarChart3 className="h-5 w-5 mr-2 text-teal-600" />
                Fundamentals
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-4">
                <div className="bg-slate-50 rounded-lg p-3 text-center">
                  <p className="text-xs text-gray-500">P/E Ratio</p>
                  <p className="font-bold">{result.fundamentals?.pe_ratio?.toFixed(2) || 'N/A'}</p>
                </div>
                <div className="bg-slate-50 rounded-lg p-3 text-center">
                  <p className="text-xs text-gray-500">Forward P/E</p>
                  <p className="font-bold">{result.fundamentals?.forward_pe?.toFixed(2) || 'N/A'}</p>
                </div>
                <div className="bg-slate-50 rounded-lg p-3 text-center">
                  <p className="text-xs text-gray-500">Beta</p>
                  <p className="font-bold">{result.fundamentals?.beta?.toFixed(2) || 'N/A'}</p>
                </div>
                <div className="bg-slate-50 rounded-lg p-3 text-center">
                  <p className="text-xs text-gray-500">52W Range</p>
                  <p className="font-bold">{result.fundamentals?.['52_week_range_position']?.toFixed(0)}%</p>
                </div>
                <div className="bg-slate-50 rounded-lg p-3 text-center">
                  <p className="text-xs text-gray-500">Analyst Target</p>
                  <p className="font-bold">${result.fundamentals?.analyst_target || 'N/A'}</p>
                </div>
                <div className="bg-slate-50 rounded-lg p-3 text-center">
                  <p className="text-xs text-gray-500">Short Ratio</p>
                  <p className="font-bold">{result.fundamentals?.short_ratio?.toFixed(2) || 'N/A'}</p>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Timestamp */}
          <p className="text-center text-xs text-gray-500">
            Analysis generated on {new Date(result.timestamp).toLocaleString()} using {result.ai_model === 'gemini' ? 'Gemini 2.5 Pro' : 'Claude Sonnet'}
          </p>
        </div>
      )}
    </div>
  );
};

export default MovementAnalyzer;
